import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $createRangeSelection,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  $isRootNode,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
  FORMAT_ELEMENT_COMMAND,
} from 'lexical';
import { useEffect } from 'react';

import { $createImageNode, $isImageNode, ImageNode } from '../nodes/ImageNode';
import { $insertAndSelectNode } from '../utils/$insertAndSelectNode';

const getDOMSelection = targetWindow => (targetWindow || window).getSelection();

export const INSERT_IMAGE_COMMAND = createCommand('INSERT_IMAGE_COMMAND');

// Images sit at the root, so the rich-text handler would format the root itself (a no-op);
// store the alignment on the image first. A range selection (e.g. select all) falls through
// so the text blocks in it still get formatted.
export function registerImageAlignment(editor) {
  return editor.registerCommand(
    FORMAT_ELEMENT_COMMAND,
    format => {
      const selection = $getSelection();
      if (!$isNodeSelection(selection) && !$isRangeSelection(selection)) {
        return false;
      }
      const images = selection.getNodes().filter($isImageNode);
      images.forEach(image => image.setAlignment(format));
      return images.length > 0 && $isNodeSelection(selection);
    },
    COMMAND_PRIORITY_HIGH,
  );
}

function $inheritedAlignment(selection, focusNode) {
  if ($isNodeSelection(selection) && $isImageNode(focusNode)) {
    return focusNode.getAlignment();
  }
  const block = focusNode.getTopLevelElement();
  return $isElementNode(block) ? block.getFormatType() : undefined;
}

export function registerInsertImage(editor) {
  return editor.registerCommand(
    INSERT_IMAGE_COMMAND,
    ({ payload, nodeId }) => {
      let focusNode;

      let selection = $getSelection();

      if (!selection) {
        const rangeSelection = $createRangeSelection();
        $setSelection(rangeSelection);
        selection = rangeSelection;
      }

      if ($isRangeSelection(selection)) {
        focusNode = selection.focus.getNode();

        if ($isRootNode(focusNode)) {
          focusNode = focusNode.getLastChild();
        }
      } else if ($isNodeSelection(selection)) {
        focusNode = selection.getNodes()[0];
      } else {
        return false;
      }

      if (focusNode !== null) {
        if (nodeId != null) {
          $getRoot()
            .getChildren()
            .forEach(node => {
              if (node.__id === nodeId) {
                node.setSrc(payload.src);
              }
            });
        } else {
          $insertAndSelectNode({
            selectedNode: focusNode,
            newNode: $createImageNode({
              alignment: $inheritedAlignment(selection, focusNode),
              ...payload,
            }),
          });
        }
      }

      return true;
    },
    COMMAND_PRIORITY_EDITOR,
  );
}

export default function ImagePlugin({ captionsEnabled }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([ImageNode])) {
      throw new Error('ImagePlugin: ImageNode not registered on editor');
    }

    return mergeRegister(
      registerImageAlignment(editor),
      registerInsertImage(editor),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        event => {
          return onDragStart(event);
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        DRAGOVER_COMMAND,
        event => {
          return onDragover(event);
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        DROP_COMMAND,
        event => {
          return onDrop(event, editor);
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [captionsEnabled, editor]);

  return null;
}

const TRANSPARENT_IMAGE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const img = document.createElement('img');
img.src = TRANSPARENT_IMAGE;

function onDragStart(event) {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) {
    return false;
  }
  dataTransfer.setData('text/plain', '_');
  dataTransfer.setDragImage(img, 0, 0);
  dataTransfer.setData(
    'application/x-lexical-drag',
    JSON.stringify({
      data: {
        altText: node.__altText,
        caption: node.__caption,
        height: node.__height,
        key: node.getKey(),
        maxWidth: node.__maxWidth,
        showCaption: node.__showCaption,
        src: node.__src,
        width: node.__width,
        alignment: node.__alignment,
      },
      type: 'image',
    }),
  );

  return true;
}

function onDragover(event) {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  if (!canDropImage(event)) {
    event.preventDefault();
  }
  return true;
}

function onDrop(event, editor) {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const data = getDragImageData(event);
  if (!data) {
    return false;
  }
  event.preventDefault();

  if (canDropImage(event)) {
    const range = getDragSelection(event);
    node.remove();
    const rangeSelection = $createRangeSelection();
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range);
    }
    $setSelection(rangeSelection);

    editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
      payload: data,
    });
  }
  return true;
}

function getImageNodeInSelection() {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) {
    return null;
  }
  const nodes = selection.getNodes();
  const node = nodes[0];
  return $isImageNode(node) ? node : null;
}

function getDragImageData(event) {
  const dragData = event.dataTransfer?.getData('application/x-lexical-drag');
  if (!dragData) {
    return null;
  }
  const { type, data } = JSON.parse(dragData);
  if (type !== 'image') {
    return null;
  }

  return data;
}

function canDropImage(event) {
  const target = event.target;
  return !!(
    target &&
    target instanceof HTMLElement &&
    !target.closest('code, span.editor-image') &&
    target.parentElement &&
    target.parentElement.closest('div.ContentEditable__root')
  );
}

function getDragSelection(event) {
  let range;
  const target = event.target;
  const targetWindow =
    target == null
      ? null
      : target.nodeType === 9
        ? target.defaultView
        : target.ownerDocument.defaultView;
  const domSelection = getDOMSelection(targetWindow);
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY);
  } else if (event.rangeParent && domSelection !== null) {
    domSelection.collapse(event.rangeParent, event.rangeOffset || 0);
    range = domSelection.getRangeAt(0);
  } else {
    throw Error(`Cannot get the selection when dragging`);
  }

  return range;
}
